import type { VoucherCodeResponse } from '@/api/model';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { isDateInPast } from '@/lib/utils';
import React from 'react';

export interface VoucherCodesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voucherTitle: string;
  codes: VoucherCodeResponse[];
  isLoading: boolean;
  onRelease?: (codeId: string) => void;
  onUse?: (codeId: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  voucherValidUntil?: string | Date | null;
}

export const VoucherCodesModal: React.FC<VoucherCodesModalProps> = ({
  open,
  onOpenChange,
  voucherTitle,
  codes,
  isLoading,
  onRelease,
  onUse,
  hasMore,
  onLoadMore,
  isLoadingMore,
  voucherValidUntil,
}) => {
  const getStatus = (code: VoucherCodeResponse) => {
    if (code.redeemed_at) return 'Eingelöst';
    if (voucherValidUntil) {
      const validUntilDate = typeof voucherValidUntil === 'string' ? new Date(voucherValidUntil) : voucherValidUntil;
      if (!Number.isNaN(validUntilDate.getTime()) && isDateInPast(validUntilDate)) return 'Abgelaufen';
    }
    if (code.activated_at) return 'Aktiviert';
    if (!code.user_id) return 'Verfügbar';
    return 'Aktiviert';
  };

  const statusToVariant = (status: string) => {
    switch (status) {
      case 'Eingelöst':
        return 'destructive' as const;
      case 'Abgelaufen':
        return 'destructive' as const;
      case 'Aktiviert':
        return 'secondary' as const;
      case 'Verfügbar':
        return 'default' as const;
      default:
        return 'default' as const;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='bg-white w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] flex flex-col rounded-xl border border-neutral-hellgrau gap-4 sm:gap-6 p-4 sm:p-6'>
        <DialogHeader className='gap-2 sm:gap-3 text-left'>
          <DialogTitle className='text-base sm:text-lg font-semibold break-words'>
            Gutscheincodes - {voucherTitle}
          </DialogTitle>
        </DialogHeader>
        <div className='flex-1 overflow-hidden flex flex-col gap-4 sm:gap-6'>
          <div className='flex-1 overflow-auto rounded-xl border border-neutral-hellgrau bg-white -mx-1 sm:mx-0'>
            <Table className='min-w-[600px] sm:min-w-[800px]'>
              <TableHeader className='bg-swd-hellblau'>
                <TableRow>
                  <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4'>
                    Code
                  </TableHead>
                  <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4'>
                    Status
                  </TableHead>
                  <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 hidden sm:table-cell'>
                    Aktiviert am
                  </TableHead>
                  <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 hidden md:table-cell'>
                    Eingelöst am
                  </TableHead>
                  <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-center text-xs sm:text-sm px-2 sm:px-4'>
                    Aktionen
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center text-neutral-grau py-8 text-sm'>
                      Wird geladen...
                    </TableCell>
                  </TableRow>
                ) : codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center text-neutral-grau py-8 text-sm'>
                      Keine Codes gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  codes.map(code => {
                    const status = getStatus(code);
                    const canRelease = status === 'Aktiviert';
                    const canUse = status === 'Aktiviert' || status === 'Verfügbar';
                    return (
                      <TableRow key={code.id}>
                        <TableCell className='font-mono text-xs sm:text-sm text-neutral-dunkelgrau px-2 sm:px-4'>
                          <div className='break-all'>{code.code}</div>
                        </TableCell>
                        <TableCell className='px-2 sm:px-4'>
                          <Badge variant={statusToVariant(status)} className='text-xs'>
                            {status}
                          </Badge>
                        </TableCell>
                        <TableCell className='text-xs sm:text-sm text-neutral-grau px-2 sm:px-4 hidden sm:table-cell'>
                          {code.activated_at ? (
                            <DateTimeDisplay date={code.activated_at} format='full' />
                          ) : (
                            <span className='text-neutral-mittelgrau'>-</span>
                          )}
                        </TableCell>
                        <TableCell className='text-xs sm:text-sm text-neutral-grau px-2 sm:px-4 hidden md:table-cell'>
                          {code.redeemed_at ? (
                            <DateTimeDisplay date={code.redeemed_at} format='full' />
                          ) : (
                            <span className='text-neutral-mittelgrau'>-</span>
                          )}
                        </TableCell>
                        <TableCell className='text-center px-2 sm:px-4'>
                          <div className='flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2'>
                            {onRelease && (
                              <Button
                                size='sm'
                                variant='outline'
                                disabled={!canRelease}
                                onClick={() => onRelease(code.id)}
                                className='h-7 sm:h-8 rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-xs sm:text-sm font-medium tracking-wide px-2 sm:px-3 hover:bg-neutral-hellgrau/50 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto'
                              >
                                Freigeben
                              </Button>
                            )}
                            {onUse && (
                              <Button
                                size='sm'
                                variant='outline'
                                disabled={!canUse}
                                onClick={() => onUse(code.id)}
                                className='h-7 sm:h-8 rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-xs sm:text-sm font-medium tracking-wide px-2 sm:px-3 hover:bg-neutral-hellgrau/50 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto'
                              >
                                <span className='hidden sm:inline'>Als eingelöst markieren</span>
                                <span className='sm:hidden'>Eingelöst</span>
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {hasMore && onLoadMore && (
            <div className='flex justify-center pt-3 sm:pt-4 border-t border-neutral-hellgrau'>
              <Button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                size='sm'
                variant='outline'
                className='h-8 sm:h-9 rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-xs sm:text-sm font-medium tracking-wide px-3 sm:px-4 hover:bg-neutral-hellgrau/50 w-full sm:w-auto'
              >
                {isLoadingMore ? 'Wird geladen...' : 'Mehr anzeigen'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
